// export default ScheduleInspection

import * as React from 'react';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TablePagination from '@mui/material/TablePagination';
import { Box, Container, Divider, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import LoadingOverlay from 'src/components/common/LoadingOverlay';
import toast from 'react-hot-toast';
import { handleError } from 'src/redux-store/slices/errorSlice';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/redux-store/appStore';
import { httpRequest } from 'src/services/httpRequest';
import ApplicationStatus from '../../../../ui-component/ApplicationStatus';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { format } from 'date-fns';
import ApplicationListFilter from '../../../../ui-component/ApplicationListFilter';
import { useLocation, } from 'react-router-dom';
import '../../../../ui-component/ApplicationListTable/Table.css';
import Breadcrumb from 'src/ui-component/Breadcrumb';
import InspectorDetailsModal from './InspectorDetailsModal';
import AssignInspectorModal from './AssignInspectorModal';
import ReassignInspectorModal from './ReassignInspectorModal';

// Custom styled component for table cells with different styles for header and body
const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.primary.main || '#6E759F',
        color: theme.palette.common.white,
        fontSize: '0.9rem',
        padding: '8px',
        textTransform: 'capitalize'
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        padding: '8px'
    }
}));

// Custom styled component for table rows
const StyledTableRow = styled(TableRow)(() => ({
    '&:last-child td, &:last-child th': {
        border: 0
    }
}));

/**
 * CustomTable Component
 * @param {string[]} columns - Array of column names to display
 * @param {boolean} isDraft - Flag to indicate if showing draft applications
 */
function CustomTable({ columns = [], isDraft = false, applicationID }) {
    // State management for pagination and data
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const [TotalDataCount, setTotalDataCount] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [rows, setRows] = React.useState([]);
    const [selectVal, setSelectVal] = React.useState('ALL');
    const dispatch = useDispatch();
    // const userDetails = useSelector((store: RootState) => store.user.userDetails);
    const permitDetails = useSelector(
        (store: RootState) => store.currentPermit.currentPermitDetails
    );

    // Remove first column for draft applications
    const displayColumns = isDraft ? columns.slice(1) : columns;

    /**
     * Handle page change in pagination
     * @param {Event} _event - Event object (unused)
     * @param {number} newPage - New page number
     */
    const handleChangePage = (_event, newPage) => {
        setPage(newPage);
    };

    /**
     * Handle rows per page change in pagination
     * @param {Event} event - Event containing new rows per page value
     */
    const handleChangeRowsPerPage = (event: any) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Inside the CustomTable component, add these states
    const [openModal, setOpenModal] = React.useState(false);
    const [inspectorDetails, setInspectorDetails] = React.useState({
        name: '',
        mobileNumber: '',
        timeSlot: '',
        email: '',
        inspectionDate: '',
        status: '',
        inspectionNote: '',
        Documents: [],
        inspectionCheckList: []
    });

    // Add this function to handle opening the modal
    const handleViewInspector = (item) => {
        // Here you would typically fetch inspector details from your API
        // This is a mock implementation - replace with your actual API call
        setInspectorDetails({
            name: item?.InspectorDetails?.FirstName + ' ' + item?.InspectorDetails?.LastName || 'John Doe',
            mobileNumber: item?.InspectorDetails?.PhoneNumber || '+91 98765 43210',
            timeSlot: item?.InspectorAssignments[0]?.TimeSlot || '10:00 AM - 12:00 PM',
            email: item?.InspectorDetails?.EmailID,
            inspectionDate: format(new Date(item?.InspectorAssignments[0]?.InspectionDate), 'MM-dd-yyyy'),
            status: item?.Status,
            inspectionNote: item?.InspectionNote,
            Documents: item?.Documents,
            inspectionCheckList: item?.InspectionCheckList
        });
        setOpenModal(true);
    };

    // Modal states for assign and reassign
    const [assignModalOpen, setAssignModalOpen] = React.useState(false);
    const [reassignModalOpen, setReassignModalOpen] = React.useState(false);
    const [selectedApplication, setSelectedApplication] = React.useState(null);

    // Handle assign inspector for Requested status
    const handleAssignInspector = (item) => {
        setSelectedApplication(item);
        setAssignModalOpen(true);
    };

    // Handle reassign inspector for Scheduled status
    const handleReassignInspector = (item) => {
        setSelectedApplication(item);
        setReassignModalOpen(true);
    };

    /**
     * Fetch application data from the server
     * Handles data formatting and error handling
     */
    const fetchListData = async () => {
        setLoading(true);
        try {
            // Construct URL with query parameters
            // let url = `/applications/${permitDetails.id || permit}/get_all?Page=${page + 1
            //     }&Size=${rowsPerPage}&Filter=${isDraft ? 'Draft' : selectVal}`;

            // Add UserID filter for regular users
            // if (permitDetails?.currentPermitUserRole === 'Vendor') {
            //     url += `&UserID=${userDetails.UserID}`;
            // }
            const url = applicationID ? `/construction/application/inspection?Page=1&Size=10&ApplicationID=${applicationID}` :
                `/construction/application/inspection?Page=1&Size=10`
            const data = await httpRequest('GET', url);

            if (data?.status === 'success') {
                // Format row data for table display
                const formattedRows = [];
                const rowData = columns.map((item: string) => item.replace(/\s+/g, ''));

                data?.data?.Data?.forEach((item: any) => {
                    const rowObj = {};
                    rowData.forEach((rowName: string) => {
                        // Special handling for Status, Action, and LastActivityDate columns
                        if (rowName === 'Status') {
                            rowObj[rowName] = (
                                <ApplicationStatus
                                    status={item?.Status}
                                    permitId={permitDetails.id}
                                />
                            );
                        } else if (rowName === 'Action' || rowName === 'Actions') {
                            rowObj[rowName] = (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <Tooltip title="View Inspector Details" arrow>
                                        <IconButton
                                            color="info"
                                            onClick={() => handleViewInspector(item)}
                                            disabled={!item?.InspectorID}
                                        >
                                            <RemoveRedEyeIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    {permitDetails?.currentPermitUserRole === 'Front Desk Manager' && (
                                        <>
                                            {item?.Status === 'Requested' && (
                                                <Tooltip title="Assign Inspector" arrow>
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => handleAssignInspector(item)}
                                                    >
                                                        <AssignmentIndIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {item?.Status === 'Scheduled' && (
                                                <Tooltip title="Reassign Inspector" arrow>
                                                    <IconButton
                                                        color="warning"
                                                        onClick={() => handleReassignInspector(item)}
                                                    >
                                                        <SwapHorizIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </>
                                    )}
                                </Box >
                            );
                        } else if (rowName === 'LastActivityDate') {
                            rowObj[rowName] = format(new Date(item.UpdatedDate), 'MM/dd/yyyy');
                        } else if (rowName === 'PermitExpirationDate') {
                            rowObj[rowName] = item?.PermitExpirationDate
                                ? format(new Date(item?.PermitExpirationDate), 'MM/dd/yyyy')
                                : '-';
                        } else {
                            rowObj[rowName] = item[rowName] || '';
                        }
                    });
                    formattedRows.push(rowObj);
                });

                setTotalDataCount(data?.data?.TotalRecords);
                setRows(formattedRows);
            } else {
                // Handle different types of errors
                if (data?.type === 'NETWORK_ERROR') {
                    toast.error('Network error occurred');
                } else {
                    toast.error(data?.message);
                    dispatch(handleError(data?.message));
                }
            }
        } catch (error) {
            toast.error('An error occurred');
            dispatch(handleError('An error occurred'));
        } finally {
            setLoading(false);
        }
    };

    // Fetch data when filter, page, or rows per page changes
    React.useEffect(() => {
        fetchListData();
    }, [selectVal, rowsPerPage, page]);

    return (
        <>
            {loading ? (
                <LoadingOverlay />
            ) : (
                <>
                    {/* Header section with title and filters */}
                    <Grid
                        container
                        gap={2}
                        justifyContent={'space-between'}
                        alignItems={'center'}
                    >
                        <Grid item md={3} lg={3} xl={2} xs={12} sm={12}>
                            <Typography
                                fontWeight={'bold'}
                                fontSize={{ md: '1.8rem', xs: '1.4rem' }}
                                color={'#6E759F'}
                            >
                                Inspection Schedule
                            </Typography>
                        </Grid>

                        {!isDraft && (
                            <Grid item md={3} lg={3} xl={2} xs={12} sm={12}>
                                <ApplicationListFilter
                                    filterValue={selectVal}
                                    setFilterValue={setSelectVal}
                                    title={'Status'}
                                    options={
                                        [
                                            { key: 'ALL', value: 'ALL' },
                                            { key: 'Requested', value: 'Requested' },
                                            { key: 'Approved', value: 'Approved' },
                                            { key: 'Rejected', value: 'Rejected' },
                                            { key: 'Withdrawn', value: 'Withdrawn' }
                                        ]
                                    }
                                />
                            </Grid>
                        )}
                    </Grid>

                    {/* Table section */}
                    <Box mt={2} sx={{ width: '100%', overflowX: 'auto' }}>
                        <TableContainer component={Paper} className="table_container_list">
                            <Table
                                aria-label="customized table"
                                sx={{ position: 'relative' }}
                            >
                                {/* Table Header */}
                                <TableHead sx={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                    <TableRow>
                                        {displayColumns?.map((heading, index) => (
                                            <StyledTableCell
                                                key={index}
                                                align={heading !== 'Picture' ? 'center' : 'left'}
                                            >
                                                {heading}
                                            </StyledTableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>

                                {/* Table Body */}
                                <TableBody>
                                    {rows?.length > 0 ? (
                                        rows.map((row, index) => (
                                            <StyledTableRow key={index}>
                                                {Object.keys(row).map((key, keyIndex) => (
                                                    <React.Fragment key={key}>
                                                        {(!isDraft || keyIndex > 0) && key !== 'id' && (
                                                            <StyledTableCell
                                                                align="center"
                                                                sx={{
                                                                    maxWidth: '30rem'
                                                                }}
                                                            >
                                                                {row[key]}
                                                            </StyledTableCell>
                                                        )}
                                                    </React.Fragment>
                                                ))}
                                            </StyledTableRow>
                                        ))
                                    ) : (
                                        <StyledTableRow>
                                            <StyledTableCell
                                                colSpan={displayColumns.length}
                                                align="center"
                                            >
                                                <Typography variant="h6">No data found</Typography>
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    )}
                                </TableBody>
                            </Table>

                            <Divider sx={{ marginTop: 1 }} />
                        </TableContainer>
                        {/* Pagination */}
                        <TablePagination
                            sx={{ position: 'sticky', bottom: 0 }}
                            className="pagination_parent_wrapper"
                            rowsPerPageOptions={[10, 25, 50]}
                            component="div"
                            count={TotalDataCount}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </Box>

                    {/* Modals */}
                    <InspectorDetailsModal
                        open={openModal}
                        onClose={() => setOpenModal(false)}
                        inspectorDetails={inspectorDetails}
                    />

                    <AssignInspectorModal
                        open={assignModalOpen}
                        onClose={() => setAssignModalOpen(false)}
                        applicationData={selectedApplication}
                        onAssignSuccess={fetchListData}
                    />

                    <ReassignInspectorModal
                        open={reassignModalOpen}
                        onClose={() => setReassignModalOpen(false)}
                        applicationData={selectedApplication}
                        onReassignSuccess={fetchListData}
                    />
                </>
            )}
        </>
    );
}

interface ScheduledInspectionListProps {
    applicationID?: string;
}

const ScheduledInspectionList: React.FC<ScheduledInspectionListProps> = ({ applicationID }) => {

    // Check if user logged in or not
    //useLoginCheck();
    const location = useLocation();
    const isDraft = location.pathname.split('/').pop() === 'draft-application';
    const columns = [
        'Application Number',
        'Subcode Name',
        'Inspection Type',
        'Status',
        'Last Activity Date',
        'Actions'
    ];
    return (
        <Container sx={{ mt: 2 }} className="main-table-container">
            {!applicationID && <Breadcrumb
                PermitName={'Construction'}
                PageTitle={`Inspection Schedule`}
            />}

            <Box mt={2}>
                <CustomTable columns={columns} isDraft={isDraft} applicationID={applicationID} />
            </Box>
        </Container>
    );
};

export default ScheduledInspectionList;